import { useEffect, useMemo, useState } from 'react';
import {
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import {
  Alert,
  alpha,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  AddRounded,
  AssignmentIndRounded,
  BlockRounded,
  CheckCircleRounded,
  CloseRounded,
  EditCalendarRounded,
  EditRounded,
  FlagRounded,
  FolderRounded,
  HourglassBottomRounded,
  KeyboardArrowDownRounded,
  ExpandLessRounded,
  ExpandMoreRounded,
  PersonRounded,
  PlayArrowRounded,
  PlaylistAddCheckRounded,
  RecordVoiceOverRounded,
  TipsAndUpdatesRounded,
  TimelineRounded,
  WorkspacesRounded,
} from '@mui/icons-material';
import IndustrialWorkflowCaseCompleteStageDialog from './IndustrialWorkflowCaseCompleteStageDialog';
import IndustrialWorkflowCaseActivityPanel from './IndustrialWorkflowCaseActivityPanel';
import IndustrialWorkflowCasePlanningDialog from './IndustrialWorkflowCasePlanningDialog';
import IndustrialWorkflowCaseWorkReportsPanel from './IndustrialWorkflowCaseWorkReportsPanel';
import IndustrialWorkflowStageDeadlineDialog from './IndustrialWorkflowStageDeadlineDialog';
import IndustrialWorkflowTaskDeadlineDialog from './IndustrialWorkflowTaskDeadlineDialog';
import IndustrialWorkflowDatePicker, {
  formatOperationalDate,
  formatOperationalRelativeDay,
  getOperationalDayDifference,
  toEndOfLocalDayIso,
} from './IndustrialWorkflowDatePicker';
import {
  claimIndustrialWorkflowTask,
  completeIndustrialWorkflowTask,
  createIndustrialWorkflowExternalFollowUp,
  createIndustrialWorkflowTask,
  createIndustrialWorkflowTaskFromSuggestedTemplate,
  getIndustrialWorkflowCaseStageSchedule,
  listIndustrialWorkflowCaseEvents,
  listIndustrialWorkflowMyWorkAreaMemberships,
  listIndustrialWorkflowStageTaskTemplates,
  listIndustrialWorkflowTasks,
  listIndustrialWorkflowWorkAreas,
  listIndustrialWorkflowWorkReports,
  startIndustrialWorkflowTask,
  updateIndustrialWorkflowTask,
} from '../services/industrialWorkflowsService';
import type {
  IndustrialWorkflowAssignableUser,
  IndustrialWorkflowCase,
  IndustrialWorkflowCaseEvent,
  IndustrialWorkflowCaseStageSchedule,
  IndustrialWorkflowExternalFollowUpCreatePayload,
  IndustrialWorkflowStageDefinition,
  IndustrialWorkflowStageTaskTemplate,
  IndustrialWorkflowTask,
  IndustrialWorkflowTaskCreatePayload,
  IndustrialWorkflowTaskPriority,
  IndustrialWorkflowTaskStatus,
  IndustrialWorkflowWorkArea,
  IndustrialWorkflowWorkReport,
} from '../types/industrialWorkflows';

type IndustrialWorkflowCaseTasksDialogProps = {
  workflowCase: IndustrialWorkflowCase | null;
  stages: IndustrialWorkflowStageDefinition[];
  assignableUsers: IndustrialWorkflowAssignableUser[];
  currentUserId: string | null;
  canOperateTasks: boolean;
  canManageTasks: boolean;
  canManageCases: boolean;
  onOpenHistory: () => void;
  onEditCase: () => void;
  onStageCompleted: () => void;
  onClose: () => void;
};

type TaskFormState = {
  title: string;
  description: string;
  work_area_id: string;
  assigned_user_id: string;
  priority: IndustrialWorkflowTaskPriority;
  due_at: string;
};

const EMPTY_TASK_FORM: TaskFormState = {
  title: '',
  description: '',
  work_area_id: '',
  assigned_user_id: '',
  priority: 'normal',
  due_at: '',
};

type ExternalFollowUpFormState = {
  note: string;
  next_review_at: string;
};

const EMPTY_EXTERNAL_FOLLOW_UP_FORM: ExternalFollowUpFormState = {
  note: '',
  next_review_at: '',
};

const TASK_STATUS_OPTIONS: Array<{
  value: IndustrialWorkflowTaskStatus;
  label: string;
}> = [
  {
    value: 'pending',
    label: 'Pendiente',
  },
  {
    value: 'in_progress',
    label: 'En curso',
  },
  {
    value: 'blocked',
    label: 'Bloqueada',
  },
  {
    value: 'completed',
    label: 'Completada',
  },
  {
    value: 'cancelled',
    label: 'Cancelada',
  },
];

function getErrorMessage(error: unknown) {
  if (
    typeof error === 'object' &&
    error !== null &&
    'response' in error
  ) {
    const response = (
      error as {
        response?: {
          data?: {
            detail?: string;
          };
        };
      }
    ).response;

    if (response?.data?.detail) {
      return response.data.detail;
    }
  }

  if (error instanceof Error) {
    return error.message;
  }

  return 'No se pudo completar la operación.';
}

function formatDateTime(value: string | null) {
  if (!value) return 'Sin fecha';

  return new Intl.DateTimeFormat('es-ES', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(new Date(value));
}

function formatRelativeTaskDeadline(value: string | null) {
  const relativeText =
    formatOperationalRelativeDay(value);

  if (!relativeText) {
    return value
      ? 'Fecha no disponible'
      : 'Sin fecha';
  }

  if (relativeText.startsWith('hace ')) {
    return `Vencida · ${relativeText}`;
  }

  if (relativeText === 'hoy') {
    return 'Vence hoy';
  }

  if (relativeText === 'mañana') {
    return 'Vence mañana';
  }

  return `Vence · ${relativeText}`;
}

function getTaskDeadlineTone(value: string | null) {
  const differenceInDays =
    getOperationalDayDifference(value);

  if (differenceInDays === null) {
    return '#94a3b8';
  }

  if (differenceInDays < 0) {
    return '#f87171';
  }

  if (differenceInDays === 0) {
    return '#fbbf24';
  }

  return '#cbd5e1';
}

function isOutsideSystemWaitTask(
  task: IndustrialWorkflowTask,
) {
  return task.task_type === 'outside_system_wait';
}

function formatRelativeExternalResponseDeadline(
  value: string | null,
) {
  const relativeText =
    formatOperationalRelativeDay(value);

  if (!relativeText) {
    return value
      ? 'Fecha de respuesta no disponible'
      : 'Respuesta · sin fecha';
  }

  if (relativeText.startsWith('hace ')) {
    return `Respuesta vencida · ${relativeText}`;
  }

  return `Respuesta ${relativeText}`;
}

function formatRelativeExternalFollowUpDeadline(
  value: string | null,
) {
  const relativeText =
    formatOperationalRelativeDay(value);

  if (!relativeText) {
    return value
      ? 'Fecha de revisión no disponible'
      : 'Sin próxima revisión';
  }

  if (relativeText.startsWith('hace ')) {
    return `Seguimiento vencido · ${relativeText}`;
  }

  return `Seguimiento ${relativeText}`;
}

function getStageModeLabel(
  stage: IndustrialWorkflowStageDefinition | undefined,
) {
  if (!stage) return 'Etapa no disponible';

  if (
    stage.stage_type === 'closure'
    || stage.is_final
  ) {
    return 'Cierre';
  }

  if (stage.stage_type === 'outside_system_wait') {
    return 'Espera externa';
  }

  return 'Trabajo interno';
}

function getStageDeadlineInfo(
  value: string | null | undefined,
) {
  if (!value) {
    return {
      dueAt: null,
      statusLabel: 'Sin plazo configurado',
      statusColor: '#94a3b8',
    };
  }

  const differenceInDays =
    getOperationalDayDifference(value);

  if (differenceInDays === null) {
    return {
      dueAt: null,
      statusLabel: 'Plazo no disponible',
      statusColor: '#94a3b8',
    };
  }

  if (differenceInDays < 0) {
    const overdueDays =
      Math.abs(differenceInDays);

    return {
      dueAt: value,
      statusLabel:
        overdueDays === 1
          ? 'Vencida · hace 1 día'
          : `Vencida · hace ${overdueDays} días`,
      statusColor: '#f87171',
    };
  }

  if (differenceInDays === 0) {
    return {
      dueAt: value,
      statusLabel: 'Vence hoy',
      statusColor: '#fbbf24',
    };
  }

  if (differenceInDays === 1) {
    return {
      dueAt: value,
      statusLabel: 'Vence mañana',
      statusColor: '#cbd5e1',
    };
  }

  return {
    dueAt: value,
    statusLabel:
      `Quedan ${differenceInDays} días`,
    statusColor: '#cbd5e1',
  };
}

function getPlanningSummaryTone(
  schedule:
    IndustrialWorkflowCaseStageSchedule,
) {
  const status =
    schedule.active_version
      ?.calculation_status
    ?? schedule.schedule_status;

  if (status === 'conflicted') {
    return {
      label:
        'Planificación con conflictos',

      color:
        '#fecaca',

      bgcolor:
        alpha(
          '#ef4444',
          0.16,
        ),
    };
  }

  if (status === 'incomplete') {
    return {
      label:
        'Planificación incompleta',

      color:
        '#fde68a',

      bgcolor:
        alpha(
          '#f59e0b',
          0.16,
        ),
    };
  }

  if (status === 'not_generated') {
    return {
      label:
        'Planificación pendiente',

      color:
        '#cbd5e1',

      bgcolor:
        alpha(
          '#64748b',
          0.14,
        ),
    };
  }

  if (!schedule.target_due_on) {
    return {
      label:
        'Sin objetivo global',

      color:
        '#cbd5e1',

      bgcolor:
        alpha(
          '#64748b',
          0.14,
        ),
    };
  }

  const slackDays =
    schedule.active_version
      ?.slack_days;

  if (
    slackDays !== null
    && slackDays !== undefined
    && slackDays < 0
  ) {
    return {
      label:
        'Fuera de objetivo',

      color:
        '#fecaca',

      bgcolor:
        alpha(
          '#ef4444',
          0.16,
        ),
    };
  }

  if (
    slackDays !== null
    && slackDays !== undefined
    && slackDays <= 5
  ) {
    return {
      label:
        'Margen reducido',

      color:
        '#fde68a',

      bgcolor:
        alpha(
          '#f59e0b',
          0.16,
        ),
    };
  }

  return {
    label:
      'Dentro de objetivo',

    color:
      '#bbf7d0',

    bgcolor:
      alpha(
        '#22c55e',
        0.15,
      ),
  };
}

function formatPlanningSlack(
  schedule:
    IndustrialWorkflowCaseStageSchedule,
) {
  if (!schedule.target_due_on) {
    return 'Margen · sin objetivo';
  }

  const slackDays =
    schedule.active_version
      ?.slack_days;

  if (
    slackDays === null
    || slackDays === undefined
  ) {
    return 'Margen · no disponible';
  }

  if (slackDays < 0) {
    return `Retraso · ${Math.abs(
      slackDays,
    )} días laborables`;
  }

  return `Margen · ${slackDays} días laborables`;
}

function readPayloadText(
  payload: Record<string, unknown>,
  key: string,
) {
  const value = payload[key];

  return typeof value === 'string'
    ? value
    : null;
}

function getAssignableUserLabel(
  user: IndustrialWorkflowAssignableUser | undefined,
) {
  if (!user) return 'Sin responsable';

  return user.full_name || user.email || user.user_id;
}

function getWorkAreaLabel(
  workArea: IndustrialWorkflowWorkArea | undefined,
) {
  if (!workArea) return 'Área no disponible';

  return workArea.name;
}

function getPriorityLabel(
  priority: IndustrialWorkflowTaskPriority,
) {
  if (priority === 'urgent') return 'Urgente';
  if (priority === 'high') return 'Alta';
  if (priority === 'low') return 'Baja';

  return 'Normal';
}

function getPriorityTone(
  priority: IndustrialWorkflowTaskPriority,
) {
  if (priority === 'urgent') {
    return {
      color: '#fecaca',
      bgcolor: alpha('#ef4444', 0.17),
    };
  }

  if (priority === 'high') {
    return {
      color: '#fed7aa',
      bgcolor: alpha('#f97316', 0.15),
    };
  }

  if (priority === 'low') {
    return {
      color: '#cbd5e1',
      bgcolor: alpha('#64748b', 0.14),
    };
  }

  return {
    color: '#bfdbfe',
    bgcolor: alpha('#3b82f6', 0.14),
  };
}

function getTaskStatusVisual(
  status: IndustrialWorkflowTaskStatus,
) {
  if (status === 'completed') {
    return {
      label: 'Completada',
      color: '#4ade80',
      icon: <CheckCircleRounded fontSize="small" />,
    };
  }

  if (status === 'in_progress') {
    return {
      label: 'En curso',
      color: '#38bdf8',
      icon: <PlayArrowRounded fontSize="small" />,
    };
  }

  if (status === 'blocked') {
    return {
      label: 'Bloqueada',
      color: '#f87171',
      icon: <BlockRounded fontSize="small" />,
    };
  }

  if (status === 'cancelled') {
    return {
      label: 'Cancelada',
      color: '#94a3b8',
      icon: <CloseRounded fontSize="small" />,
    };
  }

  return {
    label: 'Pendiente',
    color: '#fbbf24',
    icon: <HourglassBottomRounded fontSize="small" />,
  };
}

function buildTaskPayload(
  form: TaskFormState,
): IndustrialWorkflowTaskCreatePayload {
  return {
    title: form.title.trim(),
    description: form.description.trim() || null,
    priority: form.priority,
    work_area_id:
      form.work_area_id || null,
    assigned_user_id:
      form.assigned_user_id || null,
    due_at: form.due_at
      ? toEndOfLocalDayIso(form.due_at)
      : null,
    status: 'pending',
    display_order: 0,
  };
}


function getQuickTaskAction(
  status: IndustrialWorkflowTaskStatus,
) {
  if (status === 'pending') {
    return {
      label: 'Iniciar tarea',
      nextStatus: 'in_progress' as IndustrialWorkflowTaskStatus,
      color: '#38bdf8',
      icon: <PlayArrowRounded fontSize="small" />,
    };
  }

  if (status === 'in_progress') {
    return {
      label: 'Completar tarea',
      nextStatus: 'completed' as IndustrialWorkflowTaskStatus,
      color: '#4ade80',
      icon: <CheckCircleRounded fontSize="small" />,
    };
  }

  if (status === 'blocked') {
    return {
      label: 'Reactivar tarea',
      nextStatus: 'in_progress' as IndustrialWorkflowTaskStatus,
      color: '#38bdf8',
      icon: <PlayArrowRounded fontSize="small" />,
    };
  }

  if (status === 'completed') {
    return {
      label: 'Tarea completada',
      nextStatus: null,
      color: '#4ade80',
      icon: <CheckCircleRounded fontSize="small" />,
    };
  }

  return {
    label: 'Tarea cancelada',
    nextStatus: null,
    color: '#94a3b8',
    icon: <CloseRounded fontSize="small" />,
  };
}

export default function IndustrialWorkflowCaseTasksDialog({
  workflowCase,
  stages,
  assignableUsers,
  currentUserId,
  canOperateTasks,
  canManageTasks,
  canManageCases,
  onOpenHistory,
  onEditCase,
  onStageCompleted,
  onClose,
}: IndustrialWorkflowCaseTasksDialogProps) {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const [createFormOpen, setCreateFormOpen] =
    useState(false);

  const [
    completeStageDialogOpen,
    setCompleteStageDialogOpen,
  ] = useState(false);

  const [
    planningDialogOpen,
    setPlanningDialogOpen,
  ] = useState(false);

  const [
    stageDeadlineDialogOpen,
    setStageDeadlineDialogOpen,
  ] = useState(false);

  const [
    taskPendingDeadlineAdjustment,
    setTaskPendingDeadlineAdjustment,
  ] = useState<IndustrialWorkflowTask | null>(
    null,
  );

  const [
    stageDeadlineOverrideDueOn,
    setStageDeadlineOverrideDueOn,
  ] = useState<
    string
    | null
    | undefined
  >(undefined);

  const [taskForm, setTaskForm] =
    useState<TaskFormState>(EMPTY_TASK_FORM);

  const [
    externalFollowUpDialogOpen,
    setExternalFollowUpDialogOpen,
  ] = useState(false);

  const [
    externalFollowUpForm,
    setExternalFollowUpForm,
  ] = useState<ExternalFollowUpFormState>(
    EMPTY_EXTERNAL_FOLLOW_UP_FORM,
  );

  const [taskStatusMenuAnchorEl, setTaskStatusMenuAnchorEl] =
    useState<HTMLElement | null>(null);

  const [taskStatusMenuTask, setTaskStatusMenuTask] =
    useState<IndustrialWorkflowTask | null>(null);

  const [
    showCompletedTasks,
    setShowCompletedTasks,
  ] = useState(false);

  const [
    showSuggestedActions,
    setShowSuggestedActions,
  ] = useState(false);

  const [
    requestedWorkReportId,
    setRequestedWorkReportId,
  ] = useState<string | null>(null);

  useEffect(() => {
    setCreateFormOpen(false);
    setCompleteStageDialogOpen(false);
    setPlanningDialogOpen(false);
    setStageDeadlineDialogOpen(false);
    setTaskPendingDeadlineAdjustment(null);
    setStageDeadlineOverrideDueOn(undefined);
    setExternalFollowUpDialogOpen(false);
    setExternalFollowUpForm(
      EMPTY_EXTERNAL_FOLLOW_UP_FORM,
    );
    setTaskStatusMenuAnchorEl(null);
    setTaskStatusMenuTask(null);
    setShowCompletedTasks(false);
    setShowSuggestedActions(false);
    setRequestedWorkReportId(null);
    setTaskForm(EMPTY_TASK_FORM);
  }, [workflowCase?.id]);

  const tasksQuery = useQuery<IndustrialWorkflowTask[]>({
    queryKey: [
      'industrial-workflows-tasks',
      workflowCase?.id,
    ],
    queryFn: () =>
      listIndustrialWorkflowTasks(
        workflowCase?.id as string,
      ),
    enabled: Boolean(workflowCase),
    retry: false,
    refetchOnMount: 'always',
  });

  const workReportsQuery = useQuery<
    IndustrialWorkflowWorkReport[]
  >({
    queryKey: [
      'industrial-workflows-work-reports',
      workflowCase?.id,
      true,
    ],
    queryFn: () =>
      listIndustrialWorkflowWorkReports(
        workflowCase?.id as string,
        true,
      ),
    enabled: Boolean(workflowCase),
    retry: false,
    refetchOnMount: 'always',
  });

    const caseScheduleQuery =
    useQuery({
      queryKey: [
        'industrial-workflows-case-schedule',
        workflowCase?.id,
      ],

      queryFn: () =>
        getIndustrialWorkflowCaseStageSchedule(
          workflowCase
            ?.id as string,
        ),

      enabled:
        Boolean(
          workflowCase,
        ),

      retry:
        false,

      refetchOnMount:
        'always',
    });

  const suggestedTaskTemplatesQuery =
    useQuery<
      IndustrialWorkflowStageTaskTemplate[]
    >({
      queryKey: [
        'industrial-workflows-stage-task-templates',
        workflowCase
          ?.process_definition_id,
        workflowCase
          ?.current_stage_definition_id,
        false,
      ],
      queryFn: () =>
        listIndustrialWorkflowStageTaskTemplates(
          (
            workflowCase
              ?.process_definition_id
          ) as string,
          (
            workflowCase
              ?.current_stage_definition_id
          ) as string,
          false,
        ),
      enabled:
        Boolean(
          workflowCase
          && canManageTasks,
        ),
      retry: false,
      refetchOnMount: 'always',
    });

  const workAreasQuery = useQuery({
    queryKey: [
      'industrial-workflows-work-areas',
      true,
    ],
    queryFn: () =>
      listIndustrialWorkflowWorkAreas(true),
    retry: false,
  });

  const myWorkAreaMembershipsQuery = useQuery({
    queryKey: [
      'industrial-workflows-my-work-area-memberships',
    ],
    queryFn:
      listIndustrialWorkflowMyWorkAreaMemberships,
    enabled: Boolean(workflowCase),
    retry: false,
  });

  const caseEventsQuery = useQuery<
    IndustrialWorkflowCaseEvent[]
  >({
    queryKey: [
      'industrial-workflows-case-events',
      workflowCase?.id,
    ],
    queryFn: () =>
      listIndustrialWorkflowCaseEvents(
        workflowCase?.id as string,
      ),
    enabled: Boolean(workflowCase),
    retry: false,
    refetchOnMount: 'always',
  });

  const workAreaById = useMemo(
    () =>
      new Map(
        (workAreasQuery.data ?? []).map(
          (workArea) => [
            workArea.id,
            workArea,
          ],
        ),
      ),
    [workAreasQuery.data],
  );

  const myWorkAreaIdSet = useMemo(
    () =>
      new Set(
        (
          myWorkAreaMembershipsQuery.data ?? []
        )
          .filter(
            (membership) =>
              membership.is_active,
          )
          .map(
            (membership) =>
              membership.work_area_id,
          ),
      ),
    [myWorkAreaMembershipsQuery.data],
  );

  const assignableUserById = useMemo(
    () =>
      new Map(
        assignableUsers.map((user) => [
          user.user_id,
          user,
        ]),
      ),
    [assignableUsers],
  );

  const workReportById = useMemo(
    () =>
      new Map(
        (workReportsQuery.data ?? []).map(
          (workReport) => [
            workReport.id,
            workReport,
          ],
        ),
      ),
    [workReportsQuery.data],
  );

  const currentStage =
    workflowCase
      ? stages.find(
          (stage) =>
            stage.id
            === workflowCase
              .current_stage_definition_id,
        )
      : undefined;

  const isOutsideSystemWaitStage =
    currentStage?.stage_type
    === 'outside_system_wait';

  const isClosureStage =
    currentStage?.stage_type
    === 'closure'
    || Boolean(currentStage?.is_final);

  const isInternalWorkStage =
    Boolean(currentStage)
    && !isOutsideSystemWaitStage
    && !isClosureStage;

  const trackingWorkArea =
    currentStage?.tracking_work_area_id
      ? workAreaById.get(
          currentStage.tracking_work_area_id,
        )
      : undefined;

  const executionWorkArea =
    currentStage?.execution_work_area_id
      ? workAreaById.get(
          currentStage.execution_work_area_id,
        )
      : undefined;


  const tasks = tasksQuery.data ?? [];

  const operableWorkReportIds = useMemo(
    () =>
      canOperateTasks
        ? tasks
            .filter(
              (task) =>
                Boolean(currentUserId)
                && Boolean(task.work_report_id)
                && task.assigned_user_id === currentUserId
                && task.status !== 'completed'
                && task.status !== 'cancelled',
            )
            .map(
              (task) =>
                task.work_report_id as string,
            )
        : [],
    [canOperateTasks, tasks, currentUserId],
  );

  const planningSummaryTone =
    caseScheduleQuery.data
      ? getPlanningSummaryTone(
          caseScheduleQuery.data,
        )
      : null;

  const currentStageTasks =
    useMemo(
      () =>
        tasks.filter(
          (task) => {
            if (
              workflowCase
                ?.current_stage_entry_token
            ) {
              return (
                task.stage_entry_token
                === workflowCase
                  .current_stage_entry_token
              );
            }

            return (
              task.stage_definition_id
              === workflowCase
                ?.current_stage_definition_id
            );
          },
        ),
      [
        tasks,
        workflowCase
          ?.current_stage_definition_id,
        workflowCase
          ?.current_stage_entry_token,
      ],
    );

  const activeStageTasks = useMemo(
    () =>
      currentStageTasks.filter(
        (task) =>
          task.status !== 'completed'
          && task.status !== 'cancelled',
      ),
    [currentStageTasks],
  );

  const completedStageTasks = useMemo(
    () =>
      currentStageTasks.filter(
        (task) =>
          task.status === 'completed'
          || task.status === 'cancelled',
      ),
    [currentStageTasks],
  );

  const linkedWorkReportIds = useMemo(
    () =>
      currentStageTasks
        .map((task) => task.work_report_id)
        .filter(
          (workReportId): workReportId is string =>
            Boolean(workReportId),
        ),
    [currentStageTasks],
  );

  const externalFollowUps =
    useMemo(
      () =>
        (caseEventsQuery.data ?? [])
          .filter((event) => {
            if (
              event.event_type
              !== 'external_followup_recorded'
            ) {
              return false;
            }

            const stageDefinitionId =
              readPayloadText(
                event.payload,
                'stage_definition_id',
              );

            const stageEntryToken =
              readPayloadText(
                event.payload,
                'stage_entry_token',
              );

            if (
              stageDefinitionId
              !== workflowCase
                ?.current_stage_definition_id
            ) {
              return false;
            }

            if (
              workflowCase
                ?.current_stage_entry_token
            ) {
              return (
                stageEntryToken
                === workflowCase
                  .current_stage_entry_token
              );
            }

            return true;
          })
          .sort(
            (left, right) =>
              new Date(
                right.created_at,
              ).getTime()
              - new Date(
                left.created_at,
              ).getTime(),
          ),
      [
        caseEventsQuery.data,
        workflowCase
          ?.current_stage_definition_id,
        workflowCase
          ?.current_stage_entry_token,
      ],
    );

  const currentStageEnteredAt =
    useMemo(
      () => {
        const matchingEntry =
          (caseEventsQuery.data ?? [])
            .find((event) => {
              if (
                event.event_type
                !== 'stage_entered'
              ) {
                return false;
              }

              const stageDefinitionId =
                readPayloadText(
                  event.payload,
                  'stage_definition_id',
                );

              const stageEntryToken =
                readPayloadText(
                  event.payload,
                  'stage_entry_token',
                );

              if (
                stageDefinitionId
                !== workflowCase
                  ?.current_stage_definition_id
              ) {
                return false;
              }

              if (
                workflowCase
                  ?.current_stage_entry_token
              ) {
                return (
                  stageEntryToken
                  === workflowCase
                    .current_stage_entry_token
                );
              }

              return true;
            });

        return (
          matchingEntry?.created_at
          ?? workflowCase?.updated_at
          ?? null
        );
      },
      [
        caseEventsQuery.data,
        workflowCase?.updated_at,
        workflowCase
          ?.current_stage_definition_id,
        workflowCase
          ?.current_stage_entry_token,
      ],
    );

  const stageDeadlineInfo =
    getStageDeadlineInfo(
      stageDeadlineOverrideDueOn
        !== undefined
        ? (
            stageDeadlineOverrideDueOn
              ? toEndOfLocalDayIso(
                  stageDeadlineOverrideDueOn,
                )
              : null
          )
        : (
            workflowCase
              ?.current_stage_due_at
            ?? null
          ),
    );

  const latestExternalFollowUp =
    externalFollowUps[0]
    ?? null;

  const nextExternalReviewAt =
    latestExternalFollowUp
      ? readPayloadText(
          latestExternalFollowUp.payload,
          'next_review_at',
        )
      : workflowCase?.next_review_at
        ?? null;

  const hasPendingBlockingTasks =
    currentStageTasks.some(
      (task) =>
        task.is_blocking
        && task.status !== 'completed'
        && task.status !== 'cancelled',
    );

  const suggestedTaskTemplates =
    useMemo(
      () =>
        (
          suggestedTaskTemplatesQuery
            .data ?? []
        ).filter(
          (template) =>
            template.is_active
            && template.creation_mode
              === 'suggested',
        ),
      [
        suggestedTaskTemplatesQuery
          .data,
      ],
    );

  const taskSummary = useMemo(
    () => ({
      total: currentStageTasks.length,
      pending: currentStageTasks.filter(
        (task) => task.status === 'pending',
      ).length,
      inProgress: currentStageTasks.filter(
        (task) => task.status === 'in_progress',
      ).length,
      blocked: currentStageTasks.filter(
        (task) => task.status === 'blocked',
      ).length,
      completed: currentStageTasks.filter(
        (task) => task.status === 'completed',
      ).length,
    }),
    [currentStageTasks],
  );

  const invalidateRelatedQueries = async (
    caseId: string,
  ) => {
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: [
          'industrial-workflows-tasks',
          caseId,
        ],
      }),
      queryClient.invalidateQueries({
        queryKey: [
          'industrial-workflows-case-events',
          caseId,
        ],
      }),
      queryClient.invalidateQueries({
        queryKey: [
          'industrial-workflows-work-reports',
          caseId,
        ],
      }),
      queryClient.invalidateQueries({
        queryKey: [
          'industrial-workflows-case-schedule',
          caseId,
        ],
      }),
      queryClient.invalidateQueries({
        queryKey: [
          'industrial-workflows-cases',
        ],
      }),
      queryClient.invalidateQueries({
        queryKey: [
          'industrial-workflows-operations-dashboard',
        ],
      }),
    ]);
  };

  const createTaskMutation = useMutation({
    mutationFn: ({
      caseId,
      payload,
    }: {
      caseId: string;
      payload: IndustrialWorkflowTaskCreatePayload;
    }) =>
      createIndustrialWorkflowTask(
        caseId,
        payload,
      ),
    onSuccess: async (_, variables) => {
      await invalidateRelatedQueries(
        variables.caseId,
      );

      setCreateFormOpen(false);
      setTaskForm(EMPTY_TASK_FORM);

      enqueueSnackbar(
        'Tarea creada correctamente.',
        {
          variant: 'success',
        },
      );
    },
    onError: (error) => {
      enqueueSnackbar(
        getErrorMessage(error),
        {
          variant: 'error',
        },
      );
    },
  });

  const createSuggestedTaskMutation =
    useMutation({
      mutationFn: ({
        caseId,
        taskTemplateId,
      }: {
        caseId: string;
        taskTemplateId: string;
      }) =>
        createIndustrialWorkflowTaskFromSuggestedTemplate(
          caseId,
          taskTemplateId,
        ),
      onSuccess: async (
        _,
        variables,
      ) => {
        await invalidateRelatedQueries(
          variables.caseId,
        );

        enqueueSnackbar(
          'Acción sugerida añadida como tarea.',
          {
            variant: 'success',
          },
        );
      },
      onError: (error) => {
        enqueueSnackbar(
          getErrorMessage(error),
          {
            variant: 'error',
          },
        );
      },
    });

  const createExternalFollowUpMutation =
    useMutation({
      mutationFn: ({
        caseId,
        payload,
      }: {
        caseId: string;
        payload:
          IndustrialWorkflowExternalFollowUpCreatePayload;
      }) =>
        createIndustrialWorkflowExternalFollowUp(
          caseId,
          payload,
        ),
      onSuccess: async (
        _,
        variables,
      ) => {
        await invalidateRelatedQueries(
          variables.caseId,
        );

        setExternalFollowUpDialogOpen(false);
        setExternalFollowUpForm(
          EMPTY_EXTERNAL_FOLLOW_UP_FORM,
        );

        enqueueSnackbar(
          'Seguimiento registrado correctamente.',
          {
            variant: 'success',
          },
        );
      },
      onError: (error) => {
        enqueueSnackbar(
          getErrorMessage(error),
          {
            variant: 'error',
          },
        );
      },
    });

  const shouldSuggestStageCompletion = (
    updatedTask: IndustrialWorkflowTask,
  ) =>
    canManageCases
    && updatedTask.status === 'completed'
    && updatedTask.is_blocking
    && Boolean(updatedTask.stage_entry_token)
    && updatedTask.stage_definition_id
      === workflowCase?.current_stage_definition_id
    && !tasks.some(
      (taskItem) =>
        taskItem.id !== updatedTask.id
        && taskItem.stage_entry_token
          === updatedTask.stage_entry_token
        && taskItem.is_blocking
        && taskItem.status !== 'completed'
        && taskItem.status !== 'cancelled',
    );

  const updateTaskStatusMutation = useMutation({
    mutationFn: ({
      caseId,
      taskId,
      status,
    }: {
      caseId: string;
      taskId: string;
      status: IndustrialWorkflowTaskStatus;
    }) =>
      updateIndustrialWorkflowTask(
        caseId,
        taskId,
        {
          status,
        },
      ),
    onSuccess: async (updatedTask, variables) => {
      await invalidateRelatedQueries(
        variables.caseId,
      );

      setTaskStatusMenuAnchorEl(null);
      setTaskStatusMenuTask(null);

      enqueueSnackbar(
        'Estado de tarea actualizado.',
        {
          variant: 'success',
        },
      );

      if (
        shouldSuggestStageCompletion(
          updatedTask,
        )
      ) {
        setCompleteStageDialogOpen(true);
      }
    },
    onError: (error) => {
      enqueueSnackbar(
        getErrorMessage(error),
        {
          variant: 'error',
        },
      );
    },
  });

  const claimTaskMutation = useMutation({
    mutationFn: ({
      caseId,
      taskId,
    }: {
      caseId: string;
      taskId: string;
    }) =>
      claimIndustrialWorkflowTask(
        caseId,
        taskId,
      ),
    onSuccess: async (_, variables) => {
      await invalidateRelatedQueries(
        variables.caseId,
      );

      enqueueSnackbar(
        'La tarea se ha asignado a tu usuario.',
        {
          variant: 'success',
        },
      );
    },
    onError: (error) => {
      enqueueSnackbar(
        getErrorMessage(error),
        {
          variant: 'error',
        },
      );
    },
  });

  const startTaskMutation = useMutation({
    mutationFn: ({
      caseId,
      taskId,
    }: {
      caseId: string;
      taskId: string;
    }) =>
      startIndustrialWorkflowTask(
        caseId,
        taskId,
      ),
    onSuccess: async (_, variables) => {
      await invalidateRelatedQueries(
        variables.caseId,
      );

      setTaskStatusMenuAnchorEl(null);
      setTaskStatusMenuTask(null);

      enqueueSnackbar(
        'La tarea se ha iniciado y queda asignada a tu usuario.',
        {
          variant: 'success',
        },
      );
    },
    onError: (error) => {
      enqueueSnackbar(
        getErrorMessage(error),
        {
          variant: 'error',
        },
      );
    },
  });

  const completeTaskMutation = useMutation({
    mutationFn: ({
      caseId,
      taskId,
    }: {
      caseId: string;
      taskId: string;
    }) =>
      completeIndustrialWorkflowTask(
        caseId,
        taskId,
      ),
    onSuccess: async (updatedTask, variables) => {
      await invalidateRelatedQueries(
        variables.caseId,
      );

      enqueueSnackbar(
        'Tarea completada correctamente.',
        {
          variant: 'success',
        },
      );

      if (
        shouldSuggestStageCompletion(
          updatedTask,
        )
      ) {
        setCompleteStageDialogOpen(true);
      }
    },
    onError: (error) => {
      enqueueSnackbar(
        getErrorMessage(error),
        {
          variant: 'error',
        },
      );
    },
  });

  const handleClose = () => {
    if (
      createTaskMutation.isPending ||
      createSuggestedTaskMutation.isPending ||
      createExternalFollowUpMutation.isPending ||
      updateTaskStatusMutation.isPending ||
      claimTaskMutation.isPending ||
      startTaskMutation.isPending ||
      completeTaskMutation.isPending
    ) {
      return;
    }

    setCreateFormOpen(false);
    setCompleteStageDialogOpen(false);
    setPlanningDialogOpen(false);
    setExternalFollowUpDialogOpen(false);
    setExternalFollowUpForm(
      EMPTY_EXTERNAL_FOLLOW_UP_FORM,
    );
    setTaskStatusMenuAnchorEl(null);
    setTaskStatusMenuTask(null);
    setTaskForm(EMPTY_TASK_FORM);
    onClose();
  };

  const handleCreateTask = () => {
    if (!workflowCase) return;

    if (!taskForm.title.trim()) {
      enqueueSnackbar(
        'Debes indicar el título de la tarea.',
        {
          variant: 'warning',
        },
      );

      return;
    }

    createTaskMutation.mutate({
      caseId: workflowCase.id,
      payload: buildTaskPayload(taskForm),
    });
  };

  const handleCreateExternalFollowUp = () => {
    if (!workflowCase) {
      return;
    }

    if (!externalFollowUpForm.note.trim()) {
      enqueueSnackbar(
        'Indica una nota de seguimiento.',
        {
          variant: 'warning',
        },
      );

      return;
    }

    createExternalFollowUpMutation.mutate({
      caseId: workflowCase.id,
      payload: {
        note:
          externalFollowUpForm
            .note
            .trim(),
        next_review_at:
          externalFollowUpForm
            .next_review_at
            ? toEndOfLocalDayIso(
                externalFollowUpForm
                  .next_review_at,
              )
            : null,
      },
    });
  };

  const handleUpdateTaskStatus = (
    task: IndustrialWorkflowTask,
    status: IndustrialWorkflowTaskStatus,
  ) => {
    if (
      !workflowCase
      || status === task.status
    ) {
      return;
    }

    updateTaskStatusMutation.mutate({
      caseId: workflowCase.id,
      taskId: task.id,
      status,
    });
  };

  const handleStartTask = (
    task: IndustrialWorkflowTask,
  ) => {
    if (!workflowCase) {
      return;
    }

    startTaskMutation.mutate({
      caseId: workflowCase.id,
      taskId: task.id,
    });
  };

  const handleQuickTaskProgress = (
    task: IndustrialWorkflowTask,
  ) => {
    if (
      task.status === 'pending'
      || task.status === 'blocked'
    ) {
      handleStartTask(task);
      return;
    }

    if (
      task.status === 'in_progress'
      && workflowCase
    ) {
      completeTaskMutation.mutate({
        caseId: workflowCase.id,
        taskId: task.id,
      });
    }
  };

  return (
    <>
      <Dialog
        open={Boolean(workflowCase)}
        onClose={handleClose}
        fullWidth
        maxWidth={false}
        BackdropProps={{
          sx: {
            bgcolor: 'rgba(2,6,23,0.82)',
            backdropFilter: 'blur(6px)',
          },
        }}
        PaperProps={{
          sx: {
            width: 'calc(100vw - 32px)',
            maxWidth: 1680,
            height: 'calc(100vh - 32px)',
            maxHeight: 'calc(100vh - 32px)',
            borderRadius: 3.5,
            border: `1px solid ${alpha(
              '#38bdf8',
              0.2,
            )}`,
            bgcolor: '#0d1728',
            boxShadow:
              '0 28px 80px rgba(0,0,0,0.5)',
            overflow: 'hidden',
          },
        }}
      >
        <DialogTitle sx={{ p: 0 }}>
          <Stack
            direction="row"
            spacing={1.15}
            alignItems="center"
            sx={{
              px: {
                xs: 1.6,
                md: 2,
              },
              py: 1.35,
              borderBottom: `1px solid ${alpha(
                '#94a3b8',
                0.14,
              )}`,
              bgcolor: 'rgba(15,23,42,0.78)',
            }}
          >
            <Box
              sx={{
                width: 38,
                height: 38,
                borderRadius: 2.2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#38bdf8',
                bgcolor: alpha('#0ea5e9', 0.14),
                border: `1px solid ${alpha(
                  '#38bdf8',
                  0.24,
                )}`,
              }}
            >
              <PlaylistAddCheckRounded />
            </Box>

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                sx={{
                  color: '#f8fafc',
                  fontWeight: 950,
                  fontSize: '1.08rem',
                }}
              >
                Gestión del expediente
              </Typography>

              <Typography
                sx={{
                  color: '#94a3b8',
                  fontSize: '0.78rem',
                  mt: 0.1,
                }}
              >
                Consulta la ficha, registra el trabajo y revisa la actividad.
              </Typography>
            </Box>

            <IconButton
              aria-label="Cerrar expediente"
              onClick={handleClose}
              sx={{ color: '#94a3b8' }}
            >
              <CloseRounded />
            </IconButton>
          </Stack>
        </DialogTitle>

        <DialogContent
          sx={{
            p: 0,
            overflow: 'hidden',
            minHeight: 0,
          }}
        >
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                lg: 'minmax(0, 1fr) 410px',
              },
              height: 1,
              minHeight: 0,
            }}
          >
            <Box
              sx={{
                overflowY: 'auto',
                px: {
                  xs: 1.5,
                  md: 2,
                },
                py: 1.7,
              }}
            >
              <Stack spacing={1.6}>
                <Paper
                  elevation={0}
                  sx={{
                    borderRadius: 3,
                    border: `1px solid ${alpha(
                      '#38bdf8',
                      0.18,
                    )}`,
                    bgcolor: alpha('#0f172a', 0.74),
                    px: 1.55,
                    py: 1.35,
                  }}
                >
                  <Stack
                    direction={{
                      xs: 'column',
                      md: 'row',
                    }}
                    justifyContent="space-between"
                    alignItems={{
                      xs: 'flex-start',
                      md: 'center',
                    }}
                    spacing={1}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Typography
                        sx={{
                          color: '#94a3b8',
                          fontSize: '0.68rem',
                          fontWeight: 900,
                          letterSpacing: 0.7,
                          textTransform: 'uppercase',
                        }}
                      >
                        Expediente
                      </Typography>

                      <Typography
                        sx={{
                          color: '#f8fafc',
                          fontWeight: 950,
                          fontSize: '1.22rem',
                          mt: 0.22,
                        }}
                      >
                        {workflowCase?.title ?? 'Expediente'}
                      </Typography>
                    </Box>

                    <Stack
                      direction={{
                        xs: 'column',
                        sm: 'row',
                      }}
                      spacing={0.7}
                    >
                      <Button
                        variant="outlined"
                        startIcon={
                          <TimelineRounded />
                        }
                        onClick={() =>
                          setPlanningDialogOpen(
                            true,
                          )
                        }
                        sx={{
                          fontWeight: 900,
                          textTransform: 'none',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        Planificación
                      </Button>

                      {canManageCases ? (
                        <Button
                          variant="outlined"
                          startIcon={<EditRounded />}
                          onClick={onEditCase}
                          sx={{
                            fontWeight: 900,
                            textTransform: 'none',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          Editar datos
                        </Button>
                      ) : null}
                    </Stack>
                  </Stack>

                  {caseScheduleQuery.data
                  && planningSummaryTone ? (
                    <Stack
                      direction="row"
                      spacing={0.55}
                      flexWrap="wrap"
                      useFlexGap
                      sx={{
                        mt: 1,
                        pt: 0.9,
                        borderTop: `1px solid ${alpha(
                          '#94a3b8',
                          0.12,
                        )}`,
                      }}
                    >
                      <Chip
                        size="small"
                        label={
                          planningSummaryTone
                            .label
                        }
                        sx={{
                          color:
                            planningSummaryTone
                              .color,

                          bgcolor:
                            planningSummaryTone
                              .bgcolor,

                          fontWeight:
                            900,
                        }}
                      />

                      <Chip
                        size="small"
                        label={`Objetivo · ${formatOperationalDate(
                          caseScheduleQuery
                            .data
                            .target_due_on,

                          'Sin objetivo',
                        )}`}
                        sx={{
                          color:
                            '#cbd5e1',

                          bgcolor:
                            alpha(
                              '#64748b',
                              0.12,
                            ),
                        }}
                      />

                      <Chip
                        size="small"
                        label={`Previsión · ${formatOperationalDate(
                          caseScheduleQuery
                            .data
                            .active_version
                            ?.projected_completion_on,

                          'No disponible',
                        )}`}
                        sx={{
                          color:
                            '#bae6fd',

                          bgcolor:
                            alpha(
                              '#0284c7',
                              0.15,
                            ),
                        }}
                      />

                      <Chip
                        size="small"
                        label={
                          formatPlanningSlack(
                            caseScheduleQuery
                              .data,
                          )
                        }
                        sx={{
                          color:
                            planningSummaryTone
                              .color,

                          bgcolor:
                            planningSummaryTone
                              .bgcolor,
                        }}
                      />
                    </Stack>
                  ) : null}
                </Paper>

                <Paper
                  elevation={0}
                  sx={{
                    borderRadius: 3,
                    border: `1px solid ${alpha(
                      '#8b5cf6',
                      0.22,
                    )}`,
                    bgcolor: alpha('#312e81', 0.1),
                    px: 1.35,
                    py: 1.05,
                  }}
                >
                  <Stack spacing={0.85}>
                    <Stack
                      direction={{
                        xs: 'column',
                        md: 'row',
                      }}
                      justifyContent="space-between"
                      alignItems={{
                        xs: 'flex-start',
                        md: 'center',
                      }}
                      spacing={0.7}
                    >
                      <Stack spacing={0.28}>
                        <Typography
                          sx={{
                            color: '#94a3b8',
                            fontSize: '0.66rem',
                            fontWeight: 900,
                            letterSpacing: 0.7,
                            textTransform: 'uppercase',
                          }}
                        >
                          Etapa actual
                        </Typography>

                        <Stack
                          direction="row"
                          spacing={0.65}
                          alignItems="center"
                          flexWrap="wrap"
                          useFlexGap
                        >
                          <Typography
                            sx={{
                              color: '#f8fafc',
                              fontSize: '1rem',
                              fontWeight: 950,
                            }}
                          >
                            {currentStage?.name ?? 'Etapa no disponible'}
                          </Typography>

                          <Chip
                            size="small"
                            icon={<FlagRounded />}
                            label={getStageModeLabel(currentStage)}
                            sx={{
                              color: '#ddd6fe',
                              bgcolor: alpha('#8b5cf6', 0.16),
                            }}
                          />
                        </Stack>
                      </Stack>

                      <Stack
                        direction="row"
                        spacing={0.55}
                        alignItems="center"
                        flexWrap="wrap"
                        useFlexGap
                      >
                        <Chip
                          size="small"
                          label={stageDeadlineInfo.statusLabel}
                          sx={{
                            color: stageDeadlineInfo.statusColor,
                            bgcolor: alpha(
                              stageDeadlineInfo.statusColor,
                              0.1,
                            ),
                            fontWeight: 900,
                          }}
                        />

                        {canManageCases
                        && !isClosureStage ? (
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={
                              <EditCalendarRounded />
                            }
                            onClick={() =>
                              setStageDeadlineDialogOpen(
                                true,
                              )
                            }
                            sx={{
                              textTransform: 'none',
                              fontWeight: 850,
                            }}
                          >
                            Ajustar plazo
                          </Button>
                        ) : null}
                      </Stack>
                    </Stack>

                    <Stack
                      direction="row"
                      spacing={0.6}
                      flexWrap="wrap"
                      useFlexGap
                    >
                      {isInternalWorkStage ? (
                        <Chip
                          size="small"
                          label={`Ejecuta · ${getWorkAreaLabel(
                            executionWorkArea,
                          )}`}
                          sx={{
                            color: '#bae6fd',
                            bgcolor: alpha('#0284c7', 0.14),
                          }}
                        />
                      ) : null}

                      {trackingWorkArea ? (
                        <Chip
                          size="small"
                          label={`Seguimiento · ${getWorkAreaLabel(
                            trackingWorkArea,
                          )}`}
                          sx={{
                            color: '#cbd5e1',
                            bgcolor: alpha('#64748b', 0.12),
                          }}
                        />
                      ) : null}

                      <Chip
                        size="small"
                        label={`Entrada · ${formatDateTime(
                          currentStageEnteredAt,
                        )}`}
                        sx={{
                          color: '#cbd5e1',
                          bgcolor: alpha('#64748b', 0.12),
                        }}
                      />

                      <Chip
                        size="small"
                        label={`Vencimiento · ${formatOperationalDate(
                          stageDeadlineInfo.dueAt,
                        )}`}
                        sx={{
                          color: '#cbd5e1',
                          bgcolor: alpha('#64748b', 0.12),
                        }}
                      />
                    </Stack>
                  </Stack>
                </Paper>

                {isOutsideSystemWaitStage ? (
                  <Paper
                    elevation={0}
                    sx={{
                      borderRadius: 3,
                      border: `1px solid ${alpha(
                        '#fbbf24',
                        0.28,
                      )}`,
                      bgcolor: alpha('#78350f', 0.12),
                      px: 1.45,
                      py: 1.35,
                    }}
                  >
                    <Stack spacing={1.1}>
                      <Stack
                        direction={{
                          xs: 'column',
                          md: 'row',
                        }}
                        justifyContent="space-between"
                        alignItems={{
                          xs: 'stretch',
                          md: 'center',
                        }}
                        spacing={1}
                      >
                        <Stack
                          direction="row"
                          spacing={0.8}
                          alignItems="center"
                        >
                          <RecordVoiceOverRounded
                            sx={{ color: '#fbbf24' }}
                          />

                          <Box>
                            <Typography
                              sx={{
                                color: '#f8fafc',
                                fontWeight: 950,
                              }}
                            >
                              Esperando respuesta externa
                            </Typography>

                            <Typography
                              sx={{
                                color: '#94a3b8',
                                fontSize: '0.8rem',
                                mt: 0.12,
                              }}
                            >
                              Registra los contactos y continúa cuando llegue la respuesta esperada.
                            </Typography>
                          </Box>
                        </Stack>

                        <Stack
                          direction={{
                            xs: 'column',
                            sm: 'row',
                          }}
                          spacing={0.75}
                        >
                          <Button
                            variant="outlined"
                            startIcon={<RecordVoiceOverRounded />}
                            onClick={() =>
                              setExternalFollowUpDialogOpen(true)
                            }
                            sx={{
                              textTransform: 'none',
                              fontWeight: 900,
                              borderColor: alpha('#fbbf24', 0.55),
                              color: '#fde68a',
                            }}
                          >
                            Registrar seguimiento
                          </Button>

                          {canManageCases ? (
                            <Button
                              variant="contained"
                              startIcon={<FlagRounded />}
                              onClick={() =>
                                setCompleteStageDialogOpen(true)
                              }
                              sx={{
                                textTransform: 'none',
                                fontWeight: 950,
                              }}
                            >
                              Respuesta recibida · continuar
                            </Button>
                          ) : null}
                        </Stack>
                      </Stack>

                      <Box
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: {
                            xs: '1fr',
                            md: 'repeat(2, minmax(0, 1fr))',
                          },
                          gap: 0.8,
                        }}
                      >
                        {[
                          {
                            label: 'Actor externo',
                            value:
                              currentStage?.outside_system_actor
                              || 'No especificado',
                          },
                          {
                            label: 'Acción o respuesta esperada',
                            value:
                              currentStage?.outside_system_action
                              || 'No especificada',
                          },
                          {
                            label: 'Último seguimiento',
                            value: latestExternalFollowUp
                              ? formatDateTime(
                                  latestExternalFollowUp.created_at,
                                )
                              : 'Todavía no registrado',
                          },
                          {
                            label: 'Próxima revisión interna',
                            value: nextExternalReviewAt
                              ? `${formatRelativeExternalFollowUpDeadline(
                                  nextExternalReviewAt,
                                )} · ${formatOperationalDate(
                                  nextExternalReviewAt,
                                )}`
                              : 'Sin próxima revisión',
                          },
                        ].map((item) => (
                          <Box
                            key={item.label}
                            sx={{
                              borderRadius: 2.2,
                              border: `1px solid ${alpha(
                                '#fbbf24',
                                0.13,
                              )}`,
                              bgcolor: 'rgba(2,6,23,0.18)',
                              px: 1,
                              py: 0.85,
                            }}
                          >
                            <Typography
                              sx={{
                                color: '#94a3b8',
                                fontSize: '0.68rem',
                                fontWeight: 850,
                                textTransform: 'uppercase',
                              }}
                            >
                              {item.label}
                            </Typography>

                            <Typography
                              sx={{
                                color: '#e2e8f0',
                                fontSize: '0.84rem',
                                mt: 0.25,
                              }}
                            >
                              {item.value}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    </Stack>
                  </Paper>
                ) : null}

                {isClosureStage ? (
                  <Alert
                    severity="success"
                    variant="outlined"
                  >
                    El expediente se encuentra en una etapa de cierre.
                    Consulta la actividad para revisar su trazabilidad.
                  </Alert>
                ) : null}

                {isInternalWorkStage ? (
                  <Paper
                    elevation={0}
                    sx={{
                      borderRadius: 3,
                      border: `1px solid ${alpha(
                        '#38bdf8',
                        0.18,
                      )}`,
                      bgcolor: 'rgba(15,23,42,0.46)',
                      px: 1.45,
                      py: 1.35,
                    }}
                  >
                    <Stack spacing={1.15}>
                      <Stack
                        direction={{
                          xs: 'column',
                          md: 'row',
                        }}
                        justifyContent="space-between"
                        alignItems={{
                          xs: 'stretch',
                          md: 'center',
                        }}
                        spacing={1}
                      >
                        <Box>
                          <Typography
                            sx={{
                              color: '#f8fafc',
                              fontWeight: 950,
                            }}
                          >
                            Trabajo de la etapa
                          </Typography>

                          <Typography
                            sx={{
                              color: '#94a3b8',
                              fontSize: '0.8rem',
                              mt: 0.12,
                            }}
                          >
                            Gestiona las tareas asignables de esta etapa.
                            Cuando una tarea requiere un parte, el registro
                            documentado se gestiona desde la propia tarea.
                          </Typography>
                        </Box>

                        <Stack
                          direction={{
                            xs: 'column',
                            sm: 'row',
                          }}
                          spacing={0.75}
                        >
                          {canManageCases ? (
                            <Button
                              variant="outlined"
                              startIcon={<FlagRounded />}
                              disabled={hasPendingBlockingTasks}
                              onClick={() =>
                                setCompleteStageDialogOpen(true)
                              }
                              sx={{
                                textTransform: 'none',
                                fontWeight: 900,
                              }}
                            >
                              Continuar etapa
                            </Button>
                          ) : null}

                          {canManageTasks ? (
                            <Button
                              variant="outlined"
                              startIcon={<AddRounded />}
                              onClick={() =>
                                setCreateFormOpen(
                                  (current) => !current,
                                )
                              }
                              sx={{
                                textTransform: 'none',
                                fontWeight: 900,
                              }}
                            >
                              Nueva tarea
                            </Button>
                          ) : null}
                        </Stack>
                      </Stack>

                      <Stack
                        direction="row"
                        spacing={0.65}
                        flexWrap="wrap"
                        useFlexGap
                      >
                        {[
                          taskSummary.pending
                            ? `Pendientes ${taskSummary.pending}`
                            : null,
                          taskSummary.inProgress
                            ? `En curso ${taskSummary.inProgress}`
                            : null,
                          taskSummary.blocked
                            ? `Bloqueadas ${taskSummary.blocked}`
                            : null,
                          taskSummary.completed
                            ? `Finalizadas ${taskSummary.completed}`
                            : null,
                        ]
                          .filter(
                            (label): label is string =>
                              Boolean(label),
                          )
                          .map((label) => (
                          <Chip
                            key={label}
                            size="small"
                            label={label}
                            sx={{
                              color: '#cbd5e1',
                              bgcolor: alpha('#64748b', 0.12),
                            }}
                          />
                        ))}
                      </Stack>

                      {createFormOpen ? (
                        <Paper
                          elevation={0}
                          sx={{
                            borderRadius: 2.5,
                            border: `1px solid ${alpha(
                              '#38bdf8',
                              0.2,
                            )}`,
                            bgcolor: 'rgba(2,6,23,0.18)',
                            px: 1.15,
                            py: 1.05,
                          }}
                        >
                          <Stack spacing={1}>
                            <Stack
                              direction="row"
                              justifyContent="space-between"
                              alignItems="center"
                            >
                              <Typography
                                sx={{
                                  color: '#f8fafc',
                                  fontWeight: 900,
                                }}
                              >
                                Nueva tarea
                              </Typography>

                              <IconButton
                                size="small"
                                onClick={() => {
                                  setCreateFormOpen(false);
                                  setTaskForm(EMPTY_TASK_FORM);
                                }}
                                sx={{ color: '#94a3b8' }}
                              >
                                <CloseRounded fontSize="small" />
                              </IconButton>
                            </Stack>

                            {workAreasQuery.isError ? (
                              <Alert
                                severity="warning"
                                variant="outlined"
                              >
                                No se pudieron cargar las áreas.
                              </Alert>
                            ) : null}

                            <TextField
                              size="small"
                              label="Título"
                              value={taskForm.title}
                              onChange={(event) =>
                                setTaskForm((current) => ({
                                  ...current,
                                  title: event.target.value,
                                }))
                              }
                              required
                              fullWidth
                            />

                            <TextField
                              size="small"
                              label="Descripción"
                              value={taskForm.description}
                              onChange={(event) =>
                                setTaskForm((current) => ({
                                  ...current,
                                  description: event.target.value,
                                }))
                              }
                              multiline
                              minRows={2}
                              fullWidth
                            />

                            <Box
                              sx={{
                                display: 'grid',
                                gridTemplateColumns: {
                                  xs: '1fr',
                                  md: 'repeat(2, minmax(0, 1fr))',
                                },
                                gap: 0.8,
                              }}
                            >
                              <TextField
                                select
                                size="small"
                                label="Área responsable"
                                value={taskForm.work_area_id}
                                onChange={(event) =>
                                  setTaskForm((current) => ({
                                    ...current,
                                    work_area_id: event.target.value,
                                    assigned_user_id: '',
                                  }))
                                }
                                fullWidth
                              >
                                <MenuItem value="">
                                  Sin área · asignación individual
                                </MenuItem>

                                {(workAreasQuery.data ?? [])
                                  .filter((area) => area.is_active)
                                  .map((area) => (
                                    <MenuItem
                                      key={area.id}
                                      value={area.id}
                                    >
                                      {area.name}
                                    </MenuItem>
                                  ))}
                              </TextField>

                              <TextField
                                select
                                size="small"
                                label="Responsable"
                                value={taskForm.assigned_user_id}
                                onChange={(event) =>
                                  setTaskForm((current) => ({
                                    ...current,
                                    work_area_id: '',
                                    assigned_user_id: event.target.value,
                                  }))
                                }
                                disabled={Boolean(taskForm.work_area_id)}
                                fullWidth
                              >
                                <MenuItem value="">
                                  Sin responsable
                                </MenuItem>

                                {assignableUsers.map((user) => (
                                  <MenuItem
                                    key={user.user_id}
                                    value={user.user_id}
                                  >
                                    {getAssignableUserLabel(user)}
                                  </MenuItem>
                                ))}
                              </TextField>

                              <TextField
                                select
                                size="small"
                                label="Prioridad"
                                value={taskForm.priority}
                                onChange={(event) =>
                                  setTaskForm((current) => ({
                                    ...current,
                                    priority:
                                      event.target.value as IndustrialWorkflowTaskPriority,
                                  }))
                                }
                                fullWidth
                              >
                                <MenuItem value="low">Baja</MenuItem>
                                <MenuItem value="normal">Normal</MenuItem>
                                <MenuItem value="high">Alta</MenuItem>
                                <MenuItem value="urgent">Urgente</MenuItem>
                              </TextField>

                              <IndustrialWorkflowDatePicker
                                size="small"
                                label="Fecha límite"
                                value={taskForm.due_at}
                                onChange={(value) =>
                                  setTaskForm((current) => ({
                                    ...current,
                                    due_at: value,
                                  }))
                                }
                              />
                            </Box>

                            <Stack
                              direction="row"
                              justifyContent="flex-end"
                              spacing={0.75}
                            >
                              <Button
                                onClick={() => {
                                  setCreateFormOpen(false);
                                  setTaskForm(EMPTY_TASK_FORM);
                                }}
                                sx={{ textTransform: 'none' }}
                              >
                                Cancelar
                              </Button>

                              <Button
                                variant="contained"
                                disabled={
                                  !taskForm.title.trim()
                                  || createTaskMutation.isPending
                                }
                                onClick={handleCreateTask}
                                sx={{ textTransform: 'none' }}
                              >
                                Crear tarea
                              </Button>
                            </Stack>
                          </Stack>
                        </Paper>
                      ) : null}

                      {tasksQuery.isFetching ? (
                        <Stack
                          direction="row"
                          spacing={0.8}
                          alignItems="center"
                          sx={{ color: '#94a3b8' }}
                        >
                          <CircularProgress size={17} />

                          <Typography sx={{ fontSize: '0.82rem' }}>
                            Cargando tareas...
                          </Typography>
                        </Stack>
                      ) : null}

                      {tasksQuery.isError ? (
                        <Alert severity="error" variant="outlined">
                          {getErrorMessage(tasksQuery.error)}
                        </Alert>
                      ) : null}

                      {!tasksQuery.isLoading
                      && !tasksQuery.isError
                      && !currentStageTasks.length ? (
                        <Alert severity="info" variant="outlined">
                          Todavía no hay tareas registradas en esta etapa.
                        </Alert>
                      ) : null}

                      <Stack spacing={0.75}>
                        {activeStageTasks.map((task) => {
                          const taskStatusVisual =
                            getTaskStatusVisual(task.status);

                          const isExternalWaitTask =
                            isOutsideSystemWaitTask(task);

                          const quickTaskAction =
                            getQuickTaskAction(task.status);

                          const workArea = task.work_area_id
                            ? workAreaById.get(task.work_area_id)
                            : undefined;

                          const assignedUser = task.assigned_user_id
                            ? assignableUserById.get(
                                task.assigned_user_id,
                              )
                            : undefined;

                          const assignedUserLabel =
                            getAssignableUserLabel(
                              assignedUser,
                            );

                          const isAssignedToCurrentUser =
                            Boolean(
                              currentUserId
                              && task.assigned_user_id
                                === currentUserId,
                            );

                          const isAssignedToAnotherUser =
                            Boolean(
                              task.assigned_user_id
                              && !isAssignedToCurrentUser,
                            );

                          const canClaimTask =
                            canOperateTasks
                            && !task.assigned_user_id
                            && task.status !== 'completed'
                            && task.status !== 'cancelled'
                            && (
                              !task.work_area_id
                              || myWorkAreaIdSet.has(
                                task.work_area_id,
                              )
                            );

                          const canStartTask =
                            canOperateTasks
                            && (
                              task.status === 'pending'
                              || task.status === 'blocked'
                            )
                            && !isAssignedToAnotherUser
                            && (
                              isAssignedToCurrentUser
                              || canClaimTask
                            );

                          const linkedWorkReport =
                            task.work_report_id
                              ? workReportById.get(
                                  task.work_report_id,
                                )
                              : undefined;

                          const hasLinkedWorkReport =
                            Boolean(task.work_report_id);

                          const linkedWorkReportIsCompleted =
                            !hasLinkedWorkReport
                            || linkedWorkReport?.status === 'completed';

                          const canCompleteTask =
                            canOperateTasks
                            && task.status === 'in_progress'
                            && isAssignedToCurrentUser
                            && linkedWorkReportIsCompleted;

                          const quickActionEnabled =
                            canStartTask
                            || canCompleteTask;

                          let quickActionLabel =
                            quickTaskAction.label;

                          if (isExternalWaitTask) {
                            if (
                              task.status === 'pending'
                              || task.status === 'blocked'
                            ) {
                              if (isAssignedToAnotherUser) {
                                quickActionLabel =
                                  `Asignada a ${assignedUserLabel}`;
                              } else {
                                quickActionLabel =
                                  task.status === 'blocked'
                                    ? 'Reactivar espera'
                                    : 'Registrar envío';
                              }
                            }

                            if (task.status === 'in_progress') {
                              quickActionLabel =
                                isAssignedToAnotherUser
                                  ? `En espera por ${assignedUserLabel}`
                                  : 'Respuesta recibida';
                            }
                          } else {
                            if (
                              task.status === 'pending'
                              || task.status === 'blocked'
                            ) {
                              if (isAssignedToAnotherUser) {
                                quickActionLabel =
                                  `Asignada a ${assignedUserLabel}`;
                              } else if (
                                !task.assigned_user_id
                              ) {
                                quickActionLabel =
                                  task.status === 'blocked'
                                    ? 'Asignarme y reactivar'
                                    : 'Asignarme e iniciar';
                              }
                            }

                            if (
                              task.status === 'in_progress'
                              && isAssignedToAnotherUser
                            ) {
                              quickActionLabel =
                                `En curso por ${assignedUserLabel}`;
                            }

                            if (
                              task.status === 'in_progress'
                              && isAssignedToCurrentUser
                              && hasLinkedWorkReport
                              && !linkedWorkReportIsCompleted
                            ) {
                              quickActionLabel =
                                'Completa el parte';
                            }
                          }

                          const deadlineTone =
                            getTaskDeadlineTone(
                              task.due_at,
                            );

                          const shouldShowTaskWorkArea =
                            Boolean(
                              task.work_area_id
                              && task.work_area_id
                                !== currentStage
                                  ?.execution_work_area_id,
                            );

                          return (
                            <Paper
                              key={task.id}
                              elevation={0}
                              sx={{
                                borderRadius: 2.5,
                                border: `1px solid ${alpha(
                                  taskStatusVisual.color,
                                  0.22,
                                )}`,
                                bgcolor: 'rgba(2,6,23,0.16)',
                                px: 1.15,
                                py: 1.05,
                              }}
                            >
                              <Stack
                                direction={{
                                  xs: 'column',
                                  md: 'row',
                                }}
                                justifyContent="space-between"
                                spacing={1}
                              >
                                <Stack
                                  spacing={0.7}
                                  sx={{
                                    minWidth: 0,
                                    flex: 1,
                                  }}
                                >
                                  <Stack
                                    direction="row"
                                    spacing={0.55}
                                    flexWrap="wrap"
                                    useFlexGap
                                    alignItems="center"
                                  >
                                    <Chip
                                      size="small"
                                      icon={taskStatusVisual.icon}
                                      label={taskStatusVisual.label}
                                      sx={{
                                        color: taskStatusVisual.color,
                                        bgcolor: alpha(
                                          taskStatusVisual.color,
                                          0.11,
                                        ),
                                      }}
                                    />

                                    {isExternalWaitTask ? (
                                      <Chip
                                        size="small"
                                        label="Espera externa"
                                        sx={{
                                          color: '#fde68a',
                                          bgcolor: alpha(
                                            '#f59e0b',
                                            0.12,
                                          ),
                                        }}
                                      />
                                    ) : null}

                                    {task.is_blocking ? (
                                      <Chip
                                        size="small"
                                        label="Necesaria para continuar"
                                        sx={{
                                          color: '#fde68a',
                                          bgcolor: alpha(
                                            '#f59e0b',
                                            0.12,
                                          ),
                                        }}
                                      />
                                    ) : null}

                                    {task.priority !== 'normal' ? (
                                      <Chip
                                        size="small"
                                        label={getPriorityLabel(
                                          task.priority,
                                        )}
                                        sx={getPriorityTone(
                                          task.priority,
                                        )}
                                      />
                                    ) : null}

                                    {hasLinkedWorkReport ? (
                                      <Chip
                                        size="small"
                                        icon={
                                          <PlaylistAddCheckRounded />
                                        }
                                        label={
                                          linkedWorkReport
                                            ?.status === 'completed'
                                            ? 'Parte completado'
                                            : linkedWorkReport
                                              ? 'Parte pendiente'
                                              : 'Parte asociado'
                                        }
                                        sx={{
                                          color:
                                            linkedWorkReport
                                              ?.status === 'completed'
                                              ? '#bbf7d0'
                                              : '#ddd6fe',
                                          bgcolor:
                                            linkedWorkReport
                                              ?.status === 'completed'
                                              ? alpha(
                                                  '#22c55e',
                                                  0.15,
                                                )
                                              : alpha(
                                                  '#8b5cf6',
                                                  0.16,
                                                ),
                                          fontWeight: 850,
                                        }}
                                      />
                                    ) : null}
                                  </Stack>

                                  <Box>
                                    <Typography
                                      sx={{
                                        color: '#f8fafc',
                                        fontWeight: 900,
                                      }}
                                    >
                                      {task.title}
                                    </Typography>

                                  </Box>

                                  <Stack
                                    direction="row"
                                    spacing={0.55}
                                    flexWrap="wrap"
                                    useFlexGap
                                  >
                                    {shouldShowTaskWorkArea ? (
                                      <Chip
                                        size="small"
                                        icon={<WorkspacesRounded />}
                                        label={`Área · ${getWorkAreaLabel(
                                          workArea,
                                        )}`}
                                        sx={{
                                          color: '#bae6fd',
                                          bgcolor: alpha(
                                            '#0284c7',
                                            0.16,
                                          ),
                                        }}
                                      />
                                    ) : null}

                                    <Chip
                                      size="small"
                                      icon={<PersonRounded />}
                                      label={`Responsable · ${assignedUserLabel}`}
                                      sx={{
                                        color:
                                          task.assigned_user_id
                                            ? '#e2e8f0'
                                            : '#94a3b8',
                                        bgcolor: alpha(
                                          '#64748b',
                                          0.12,
                                        ),
                                      }}
                                    />

                                    <Stack
                                      direction="row"
                                      spacing={0.45}
                                      alignItems="center"
                                      flexWrap="wrap"
                                      useFlexGap
                                    >
                                      <Chip
                                        size="small"
                                        label={
                                          isExternalWaitTask
                                            ? formatRelativeExternalResponseDeadline(
                                                task.due_at,
                                              )
                                            : formatRelativeTaskDeadline(
                                                task.due_at,
                                              )
                                        }
                                        title={
                                          task.due_at
                                            ? `${
                                                isExternalWaitTask
                                                  ? 'Respuesta esperada exacta'
                                                  : 'Vencimiento exacto'
                                              }: ${formatOperationalDate(
                                                task.due_at,
                                              )}`
                                            : undefined
                                        }
                                        sx={{
                                          color: deadlineTone,
                                          bgcolor: alpha(
                                            deadlineTone,
                                            0.1,
                                          ),
                                        }}
                                      />

                                      {canManageTasks
                                      && task.status !== 'completed'
                                      && task.status !== 'cancelled' ? (
                                        <Tooltip title="Ajustar plazo">
                                          <IconButton
                                            size="small"
                                            onClick={() =>
                                              setTaskPendingDeadlineAdjustment(
                                                task,
                                              )
                                            }
                                            sx={{
                                              width: 26,
                                              height: 26,
                                              border: `1px solid ${alpha(
                                                '#38bdf8',
                                                0.28,
                                              )}`,
                                              color: '#38bdf8',
                                              bgcolor: alpha(
                                                '#0f172a',
                                                0.34,
                                              ),
                                              '&:hover': {
                                                bgcolor: alpha(
                                                  '#0284c7',
                                                  0.14,
                                                ),
                                                borderColor: alpha(
                                                  '#38bdf8',
                                                  0.42,
                                                ),
                                              },
                                            }}
                                          >
                                            <EditCalendarRounded
                                              sx={{
                                                fontSize: 15,
                                              }}
                                            />
                                          </IconButton>
                                        </Tooltip>
                                      ) : null}
                                    </Stack>
                                  </Stack>
                                </Stack>

                                <Stack
                                  direction={{
                                    xs: 'column',
                                    sm: 'row',
                                  }}
                                  spacing={0.65}
                                  alignItems={{
                                    xs: 'stretch',
                                    sm: 'center',
                                  }}
                                  sx={{
                                    flexShrink: 0,
                                  }}
                                >
                                  {canClaimTask && workflowCase ? (
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      startIcon={<AssignmentIndRounded />}
                                      disabled={
                                        claimTaskMutation.isPending
                                        || startTaskMutation.isPending
                                      }
                                      onClick={() =>
                                        claimTaskMutation.mutate({
                                          caseId: workflowCase.id,
                                          taskId: task.id,
                                        })
                                      }
                                      sx={{
                                        textTransform: 'none',
                                        fontWeight: 850,
                                      }}
                                    >
                                      Asignarme
                                    </Button>
                                  ) : null}

                                  <Button
                                    size="small"
                                    variant="contained"
                                    startIcon={quickTaskAction.icon}
                                    disabled={
                                      !quickActionEnabled
                                      || startTaskMutation.isPending
                                      || completeTaskMutation.isPending
                                      || updateTaskStatusMutation.isPending
                                    }
                                    onClick={() =>
                                      handleQuickTaskProgress(task)
                                    }
                                    sx={{
                                      minWidth: 148,
                                      textTransform: 'none',
                                      fontWeight: 900,
                                      color: '#020617',
                                      bgcolor: quickTaskAction.color,
                                      '&:hover': {
                                        bgcolor: quickTaskAction.color,
                                        filter: 'brightness(0.94)',
                                      },
                                      '&.Mui-disabled': {
                                        color: alpha(
                                          '#f8fafc',
                                          0.72,
                                        ),
                                        bgcolor: alpha(
                                          quickTaskAction.color,
                                          0.2,
                                        ),
                                      },
                                    }}
                                  >
                                    {quickActionLabel}
                                  </Button>

                                  {hasLinkedWorkReport ? (
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      startIcon={
                                        <PlaylistAddCheckRounded />
                                      }
                                      disabled={
                                        !task.work_report_id
                                        || workReportsQuery.isLoading
                                      }
                                      onClick={() =>
                                        setRequestedWorkReportId(
                                          task.work_report_id,
                                        )
                                      }
                                      sx={{
                                        textTransform: 'none',
                                        fontWeight: 850,
                                      }}
                                    >
                                      Abrir parte
                                    </Button>
                                  ) : null}

                                  <Button
                                    size="small"
                                    variant="text"
                                    endIcon={<KeyboardArrowDownRounded />}
                                    disabled={
                                      !canManageTasks
                                      || updateTaskStatusMutation.isPending
                                      || startTaskMutation.isPending
                                    }
                                    onClick={(event) => {
                                      setTaskStatusMenuAnchorEl(
                                        event.currentTarget,
                                      );
                                      setTaskStatusMenuTask(task);
                                    }}
                                    sx={{
                                      textTransform: 'none',
                                      fontWeight: 850,
                                    }}
                                  >
                                    Más
                                  </Button>
                                </Stack>
                              </Stack>
                            </Paper>
                          );
                        })}
                      </Stack>

                      {!activeStageTasks.length
                      && completedStageTasks.length ? (
                        <Alert
                          severity="success"
                          variant="outlined"
                        >
                          No quedan tareas activas en esta etapa.
                        </Alert>
                      ) : null}

                      {completedStageTasks.length ? (
                        <Stack spacing={0.65}>
                          <Button
                            size="small"
                            variant="text"
                            startIcon={
                              showCompletedTasks
                                ? <ExpandLessRounded />
                                : <ExpandMoreRounded />
                            }
                            onClick={() =>
                              setShowCompletedTasks(
                                (current) => !current,
                              )
                            }
                            sx={{
                              alignSelf: 'flex-start',
                              textTransform: 'none',
                              fontWeight: 850,
                              color: '#94a3b8',
                            }}
                          >
                            {showCompletedTasks
                              ? 'Ocultar tareas finalizadas'
                              : `Ver tareas finalizadas (${completedStageTasks.length})`}
                          </Button>

                          {showCompletedTasks ? (
                            <Stack spacing={0.55}>
                              {completedStageTasks.map((task) => {
                                const taskStatusVisual =
                                  getTaskStatusVisual(
                                    task.status,
                                  );

                                const assignedUser =
                                  task.assigned_user_id
                                    ? assignableUserById.get(
                                        task.assigned_user_id,
                                      )
                                    : undefined;

                                return (
                                  <Stack
                                    key={task.id}
                                    direction={{
                                      xs: 'column',
                                      md: 'row',
                                    }}
                                    justifyContent="space-between"
                                    spacing={0.7}
                                    sx={{
                                      borderRadius: 2,
                                      border: `1px solid ${alpha(
                                        taskStatusVisual.color,
                                        0.13,
                                      )}`,
                                      bgcolor:
                                        'rgba(2,6,23,0.1)',
                                      px: 0.95,
                                      py: 0.75,
                                    }}
                                  >
                                    <Stack
                                      direction="row"
                                      spacing={0.65}
                                      flexWrap="wrap"
                                      useFlexGap
                                      alignItems="center"
                                    >
                                      <Chip
                                        size="small"
                                        icon={
                                          taskStatusVisual.icon
                                        }
                                        label={
                                          taskStatusVisual.label
                                        }
                                        sx={{
                                          color:
                                            taskStatusVisual.color,
                                          bgcolor: alpha(
                                            taskStatusVisual.color,
                                            0.09,
                                          ),
                                        }}
                                      />

                                      <Typography
                                        sx={{
                                          color: '#cbd5e1',
                                          fontWeight: 800,
                                          fontSize: '0.84rem',
                                        }}
                                      >
                                        {task.title}
                                      </Typography>
                                    </Stack>

                                    <Typography
                                      sx={{
                                        color: '#64748b',
                                        fontSize: '0.76rem',
                                      }}
                                    >
                                      {getAssignableUserLabel(
                                        assignedUser,
                                      )}
                                    </Typography>
                                  </Stack>
                                );
                              })}
                            </Stack>
                          ) : null}
                        </Stack>
                      ) : null}

                      {canManageTasks
                      && suggestedTaskTemplates.length ? (
                        <Paper
                          elevation={0}
                          sx={{
                            borderRadius: 2.5,
                            border: `1px solid ${alpha(
                              '#fbbf24',
                              0.16,
                            )}`,
                            bgcolor: 'rgba(2,6,23,0.12)',
                            px: 1,
                            py: 0.9,
                          }}
                        >
                          <Stack spacing={0.8}>
                            <Stack
                              direction={{
                                xs: 'column',
                                sm: 'row',
                              }}
                              justifyContent="space-between"
                              alignItems={{
                                xs: 'flex-start',
                                sm: 'center',
                              }}
                              spacing={0.7}
                            >
                              <Stack
                                direction="row"
                                spacing={0.65}
                                alignItems="center"
                              >
                                <TipsAndUpdatesRounded
                                  sx={{ color: '#fbbf24' }}
                                />

                                <Box>
                                  <Typography
                                    sx={{
                                      color: '#f8fafc',
                                      fontWeight: 900,
                                    }}
                                  >
                                    Acciones adicionales
                                  </Typography>

                                  <Typography
                                    sx={{
                                      color: '#94a3b8',
                                      fontSize: '0.76rem',
                                      mt: 0.08,
                                    }}
                                  >
                                    Opcionales · {suggestedTaskTemplates.length} disponibles
                                  </Typography>
                                </Box>
                              </Stack>

                              <Button
                                size="small"
                                variant="text"
                                endIcon={
                                  showSuggestedActions
                                    ? <ExpandLessRounded />
                                    : <ExpandMoreRounded />
                                }
                                onClick={() =>
                                  setShowSuggestedActions(
                                    (current) => !current,
                                  )
                                }
                                sx={{
                                  textTransform: 'none',
                                  fontWeight: 850,
                                  color: '#fde68a',
                                }}
                              >
                                {showSuggestedActions
                                  ? 'Ocultar'
                                  : 'Mostrar'}
                              </Button>
                            </Stack>

                            {showSuggestedActions ? (
                              <Stack spacing={0.55}>
                                {suggestedTaskTemplates.map((template) => (
                                  <Stack
                                    key={template.id}
                                    direction={{
                                      xs: 'column',
                                      md: 'row',
                                    }}
                                    justifyContent="space-between"
                                    spacing={0.7}
                                    sx={{
                                      borderRadius: 2,
                                      border: `1px solid ${alpha(
                                        '#fbbf24',
                                        0.12,
                                      )}`,
                                      px: 0.85,
                                      py: 0.72,
                                    }}
                                  >
                                    <Box>
                                      <Typography
                                        sx={{
                                          color: '#e2e8f0',
                                          fontWeight: 850,
                                          fontSize: '0.84rem',
                                        }}
                                      >
                                        {template.title}
                                      </Typography>

                                      {template.due_offset_days
                                        !== null ? (
                                        <Typography
                                          sx={{
                                            color: '#94a3b8',
                                            fontSize: '0.74rem',
                                            mt: 0.08,
                                          }}
                                        >
                                          Plazo al añadir · {template.due_offset_days} días
                                        </Typography>
                                      ) : null}
                                    </Box>

                                    <Button
                                      size="small"
                                      variant="outlined"
                                      startIcon={<AddRounded />}
                                      disabled={
                                        createSuggestedTaskMutation.isPending
                                      }
                                      onClick={() => {
                                        if (!workflowCase) {
                                          return;
                                        }

                                        createSuggestedTaskMutation.mutate({
                                          caseId: workflowCase.id,
                                          taskTemplateId: template.id,
                                        });
                                      }}
                                      sx={{
                                        textTransform: 'none',
                                        fontWeight: 850,
                                        color: '#fde68a',
                                        borderColor: alpha(
                                          '#fbbf24',
                                          0.42,
                                        ),
                                      }}
                                    >
                                      Añadir
                                    </Button>
                                  </Stack>
                                ))}
                              </Stack>
                            ) : null}
                          </Stack>
                        </Paper>
                      ) : null}
                    </Stack>
                  </Paper>
                ) : null}

                {isInternalWorkStage ? (
                  <IndustrialWorkflowCaseWorkReportsPanel
                    workflowCase={workflowCase}
                    canManageWorkReports={canManageTasks}
                    operableWorkReportIds={operableWorkReportIds}
                    hiddenWorkReportIds={linkedWorkReportIds}
                    requestedWorkReportId={requestedWorkReportId}
                    onRequestedWorkReportConsumed={() =>
                      setRequestedWorkReportId(null)
                    }
                  />
                ) : null}

                <Paper
                  elevation={0}
                  sx={{
                    borderRadius: 3,
                    border: `1px solid ${alpha(
                      '#94a3b8',
                      0.14,
                    )}`,
                    bgcolor: 'rgba(15,23,42,0.36)',
                    px: 1.45,
                    py: 1.2,
                  }}
                >
                  <Stack
                    direction={{
                      xs: 'column',
                      md: 'row',
                    }}
                    justifyContent="space-between"
                    alignItems={{
                      xs: 'flex-start',
                      md: 'center',
                    }}
                    spacing={0.8}
                  >
                    <Stack
                      direction="row"
                      spacing={0.75}
                      alignItems="center"
                    >
                      <FolderRounded
                        sx={{ color: '#94a3b8' }}
                      />

                      <Box>
                        <Typography
                          sx={{
                            color: '#f8fafc',
                            fontWeight: 900,
                          }}
                        >
                          Adjuntos
                        </Typography>

                        <Typography
                          sx={{
                            color: '#64748b',
                            fontSize: '0.78rem',
                            mt: 0.1,
                          }}
                        >
                          Los documentos del expediente aparecerán aquí.
                        </Typography>
                      </Box>
                    </Stack>

                    <Chip
                      size="small"
                      label="Disponible próximamente"
                      sx={{
                        color: '#cbd5e1',
                        bgcolor: alpha('#64748b', 0.12),
                      }}
                    />
                  </Stack>
                </Paper>
              </Stack>
            </Box>

            <Box
              sx={{
                display: {
                  xs: 'none',
                  lg: 'block',
                },
                minHeight: 0,
                borderLeft: `1px solid ${alpha(
                  '#94a3b8',
                  0.14,
                )}`,
              }}
            >
              <IndustrialWorkflowCaseActivityPanel
                caseId={workflowCase?.id}
                currentUserId={currentUserId}
                events={caseEventsQuery.data ?? []}
                stages={stages}
                assignableUsers={assignableUsers}
                isLoading={caseEventsQuery.isLoading}
                isFetching={caseEventsQuery.isFetching}
                isError={caseEventsQuery.isError}
                error={caseEventsQuery.error}
                onOpenFullHistory={onOpenHistory}
              />
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      <IndustrialWorkflowCasePlanningDialog
        workflowCase={workflowCase}
        open={planningDialogOpen}
        onClose={() =>
          setPlanningDialogOpen(
            false,
          )
        }
      />

      <Menu
        anchorEl={taskStatusMenuAnchorEl}
        open={Boolean(
          taskStatusMenuAnchorEl
          && taskStatusMenuTask,
        )}
        onClose={() => {
          setTaskStatusMenuAnchorEl(null);
          setTaskStatusMenuTask(null);
        }}
      >
        {TASK_STATUS_OPTIONS.map((statusOption) => (
          <MenuItem
            key={statusOption.value}
            selected={
              taskStatusMenuTask?.status
              === statusOption.value
            }
            disabled={
              updateTaskStatusMutation.isPending
              || startTaskMutation.isPending
              || taskStatusMenuTask?.status
                === statusOption.value
              || (
                statusOption.value === 'completed'
                && !taskStatusMenuTask
                  ?.assigned_user_id
              )
            }
            onClick={() => {
              if (!taskStatusMenuTask) return;

              if (
                statusOption.value
                === 'in_progress'
              ) {
                handleStartTask(
                  taskStatusMenuTask,
                );

                return;
              }

              handleUpdateTaskStatus(
                taskStatusMenuTask,
                statusOption.value,
              );
            }}
          >
            {statusOption.label}
          </MenuItem>
        ))}
      </Menu>

      <IndustrialWorkflowStageDeadlineDialog
        workflowCase={workflowCase}
        open={stageDeadlineDialogOpen}
        onClose={() =>
          setStageDeadlineDialogOpen(
            false,
          )
        }
        onAdjusted={async (
          result,
        ) => {
          setStageDeadlineOverrideDueOn(
            result
              .preview_after
              .new_due_on,
          );

          if (workflowCase) {
            await invalidateRelatedQueries(
              workflowCase.id,
            );
          }
        }}
      />

      <IndustrialWorkflowTaskDeadlineDialog
        workflowCase={workflowCase}
        task={
          taskPendingDeadlineAdjustment
        }
        open={Boolean(
          taskPendingDeadlineAdjustment,
        )}
        onClose={() =>
          setTaskPendingDeadlineAdjustment(
            null,
          )
        }
        onAdjusted={async () => {
          if (workflowCase) {
            await invalidateRelatedQueries(
              workflowCase.id,
            );
          }

          setTaskPendingDeadlineAdjustment(
            null,
          );
        }}
      />
      <Dialog
        open={externalFollowUpDialogOpen}
        onClose={() => {
          if (!createExternalFollowUpMutation.isPending) {
            setExternalFollowUpDialogOpen(false);
            setExternalFollowUpForm(
              EMPTY_EXTERNAL_FOLLOW_UP_FORM,
            );
          }
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          Registrar seguimiento externo
        </DialogTitle>

        <DialogContent>
          <Stack spacing={1.5} sx={{ pt: 1 }}>
            <TextField
              label="Nota de seguimiento"
              value={externalFollowUpForm.note}
              onChange={(event) =>
                setExternalFollowUpForm((current) => ({
                  ...current,
                  note: event.target.value,
                }))
              }
              multiline
              minRows={4}
              helperText="Resume el contacto realizado o la situación actual."
              fullWidth
              required
            />

            <IndustrialWorkflowDatePicker
              label="Próxima revisión"
              value={externalFollowUpForm.next_review_at}
              onChange={(value) =>
                setExternalFollowUpForm((current) => ({
                  ...current,
                  next_review_at: value,
                }))
              }
              helperText="Opcional. Indica en qué fecha conviene revisar de nuevo esta espera."
            />
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button
            disabled={
              createExternalFollowUpMutation.isPending
            }
            onClick={() => {
              setExternalFollowUpDialogOpen(false);
              setExternalFollowUpForm(
                EMPTY_EXTERNAL_FOLLOW_UP_FORM,
              );
            }}
          >
            Cancelar
          </Button>

          <Button
            variant="contained"
            disabled={
              createExternalFollowUpMutation.isPending
              || !externalFollowUpForm.note.trim()
            }
            onClick={handleCreateExternalFollowUp}
          >
            Guardar seguimiento
          </Button>
        </DialogActions>
      </Dialog>

      <IndustrialWorkflowCaseCompleteStageDialog
        workflowCase={workflowCase}
        stages={stages}
        open={completeStageDialogOpen}
        onClose={() =>
          setCompleteStageDialogOpen(false)
        }
        onCompleted={() => {
          setCompleteStageDialogOpen(false);
          onStageCompleted();
        }}
      />
    </>
  );
}
